import { swReadyState } from './swReadyState';
import {
    ILoadSecureMediaResult,
    loadSecureMediaAsObjectUrl,
    releaseObjectUrl,
} from './secureMedia';
import { errorUploader } from '@/lib/errorUploader';
import type { ISecureMediaFallbackExtraCtx } from '@/interface/errorUpload';

/**
 * 决议媒体 URL 为可直接消费的链接：
 * - SW 可用 → 原 URL 不变（由 SW 透明拦截解密）
 * - SW 不可用 → 走主线程降级路径，fetch + 解密 + Blob URL
 * - 失败 → null（调用方负责错误态）
 *
 * 这是 `secureMedia` 模块的统一对外入口，所有消费者（播放器 / 指令 / defaultBg）
 * 应优先使用本方法而非直接调 `loadSecureMediaAsObjectUrl`。
 *
 * @param url 原始加密媒体 URL
 * @param errorCtx 触发降级的上报上下文（可选）
 * @returns 可直接消费的 URL（可能是原 URL / blob URL），失败时返回 null
 */
export const resolveMediaUrl = async (
    url: string,
    errorCtx?: ISecureMediaFallbackExtraCtx
): Promise<string | null> => {

    const ready = await swReadyState.await( 1000 );
    if ( ready ) {

        return url;

    }

    try {

        const result = await loadSecureMediaAsObjectUrl( url );
        return result.blobUrl;

    } catch ( error ) {

        console.error( '[secureMedia] resolveMediaUrl failed', error );
        if ( errorCtx ) {

            errorUploader.uploadSecureMediaFallbackError( url, error, errorCtx );

        }
        return null;

    }

};

/**
 * 释放 blob URL。
 *
 * 对非 `blob:` 协议的 URL 调用是空操作。
 *
 * @param url 需要释放的 URL，如果 `resolveMediaUrl` 返回的是 blob URL 则应传入
 */
export const releaseMediaUrl = ( url: string | null | undefined ): void => {

    releaseObjectUrl( url );

};

// 重新导出底层 API，方便有特殊需求的消费者直接使用
export { loadSecureMediaAsObjectUrl, releaseObjectUrl };
export type { ILoadSecureMediaResult };
